// Signalements pendant un rallye : dangers (nid de poule, obstacle…) visibles par tous et annoncés à
// la voix en s'en approchant ; SOS visible seulement par le staff (politiques RLS, migration 0021).
// Logique pure, testée dans __tests__/roadAlerts.test.ts.

import { distanceM, type LatLng, snapToRoute } from './geo';
import { t } from './i18n';

export type RoadAlertKind = 'sos' | 'pothole' | 'obstacle' | 'accident' | 'works' | 'danger';

export type RoadAlert = {
  id: string;
  event_id: string;
  user_id: string;
  kind: RoadAlertKind;
  lat: number;
  lon: number;
  created_at: string;
  resolved_at: string | null;
};

/**
 * Dangers proposés au participant (le SOS est à part) : un seul choix « Danger », plus simple en roulant.
 * Les autres types restent affichés pour les signalements déjà faits.
 */
export const DANGER_KINDS: Exclude<RoadAlertKind, 'sos'>[] = ['danger'];

/** Un signalement disparaît de lui-même après ce délai (rallye d'une journée). */
export const ALERT_VISIBLE_MS = 8 * 3600 * 1000;
/** Annonce vocale d'un danger devant soi sur le tracé, à cette distance au plus (m). */
export const ANNOUNCE_AHEAD_M = 400;
/** Hors du tracé : annonce d'un danger à vol d'oiseau à cette distance au plus (m). */
export const ANNOUNCE_NEAR_M = 250;
/** Un danger à plus de N m du tracé n'est pas « sur » le tracé. */
const ON_ROUTE_M = 40;

export function alertEmoji(kind: RoadAlertKind): string {
  switch (kind) {
    case 'sos':
      return '🆘';
    case 'pothole':
      return '🕳️';
    case 'obstacle':
      return '🪨';
    case 'accident':
      return '💥';
    case 'works':
      return '🚧';
    default:
      return '⚠️';
  }
}

export function alertLabel(kind: RoadAlertKind): string {
  switch (kind) {
    case 'sos':
      return t('SOS', 'SOS');
    case 'pothole':
      return t('Nid de poule', 'Pothole');
    case 'obstacle':
      return t('Obstacle sur la route', 'Obstacle on the road');
    case 'accident':
      return t('Accident', 'Accident');
    case 'works':
      return t('Travaux', 'Roadworks');
    default:
      return t('Danger', 'Hazard');
  }
}

/** Signalements à afficher : non résolus, récents ; le SOS seulement si demandé (staff). */
export function activeAlerts(alerts: RoadAlert[], now = Date.now(), withSos = false): RoadAlert[] {
  return alerts.filter(
    (a) => !a.resolved_at && now - Date.parse(a.created_at) < ALERT_VISIBLE_MS && (withSos || a.kind !== 'sos'),
  );
}

/** Ajoute, remplace (résolution) ou retire un signalement reçu en temps réel. */
export function mergeAlert(alerts: RoadAlert[], a: RoadAlert, deleted = false): RoadAlert[] {
  const rest = alerts.filter((x) => x.id !== a.id);
  return deleted ? rest : [a, ...rest];
}

/**
 * Prochain danger à annoncer : sur le tracé devant soi (au plus ANNOUNCE_AHEAD_M), ou hors du tracé
 * à moins de ANNOUNCE_NEAR_M. Ceux déjà annoncés (`announced`) et les siens sont ignorés.
 */
export function upcomingAlert(input: {
  alerts: RoadAlert[];
  route: LatLng[];
  cum: number[];
  /** progression sur le tracé (m), null si l'on n'y est pas (avant le départ, hors parcours) */
  progress: number | null;
  position: LatLng;
  announced: Set<string>;
  me: string | null;
  now?: number;
}): { alert: RoadAlert; distance: number } | null {
  const { route, cum, progress, position, announced, me } = input;
  let best: { alert: RoadAlert; distance: number } | null = null;
  for (const a of activeAlerts(input.alerts, input.now)) {
    if (announced.has(a.id) || a.user_id === me) continue;
    const at = { latitude: a.lat, longitude: a.lon };
    let distance: number | null = null;
    if (progress != null) {
      const snap = snapToRoute(at, route, cum, progress, progress + ANNOUNCE_AHEAD_M);
      if (snap && snap.distance <= ON_ROUTE_M && snap.along >= progress) distance = snap.along - progress;
    } else {
      const d = distanceM(position, at);
      if (d <= ANNOUNCE_NEAR_M) distance = d;
    }
    if (distance != null && (!best || distance < best.distance)) best = { alert: a, distance };
  }
  return best;
}

/** Phrase de l'annonce vocale. */
export function alertSpeech(kind: RoadAlertKind, distance: number): string {
  const what =
    kind === 'works'
      ? t('travaux signalés', 'roadworks reported')
      : kind === 'obstacle'
        ? t('obstacle signalé', 'obstacle reported')
        : `${alertLabel(kind).toLowerCase()} ${t('signalé', 'reported')}`;
  if (distance < 60) return t(`Attention, ${what} ici.`, `Caution, ${what} here.`);
  const m = Math.round(distance / 50) * 50;
  return t(`Attention, ${what} dans ${m} mètres.`, `Caution, ${what} in ${m} metres.`);
}
